// calls-pg-contract-v1 — single source of truth for the /#/calls list query
// params.
//
// The classic pagination break (and the first thing to rule out when "every page
// shows the same rows"): the client sends `?page=N` but the server reads a
// DIFFERENT key (e.g. `offset`), so the server always sees page 1 / offset 0 and
// returns identical rows for every page. To make that impossible, BOTH sides now
// build/read the list params from THIS object:
//   • src/api.ts  fetchCallsPaginated → buildCallsListQuery() (the query string)
//   • api/src/index.ts  GET /api/crm/calls → c.req.query(CALLS_LIST_PARAM_KEYS.*)
// callsListParams.test.ts pins the key names so a rename on either side fails CI.
//
// Pure (no React / no fetch) so it is unit-testable and importable by the Worker.

export const CALLS_LIST_PARAM_KEYS = {
  page: 'page',
  limit: 'limit',
  search: 'search',
  direction: 'direction',
  assignedTo: 'assigned_to',
  sort: 'sort',
  order: 'order',
  outcome: 'outcome',
  hasRecording: 'has_recording',
  hasTranscript: 'has_transcript',
} as const

export type CallsListQueryOpts = {
  page?: number
  limit?: number
  search?: string
  direction?: string
  assignedTo?: number
  sort?: string
  order?: string
  outcome?: string
  has_recording?: boolean
  has_transcript?: boolean
}

/** Build the URLSearchParams the calls-list GET sends. Only truthy values are
 *  set (an empty filter is omitted, not sent as ''). Pure + tested so the param
 *  NAMES can never silently drift from what the route reads. */
export function buildCallsListQuery(opts?: CallsListQueryOpts): URLSearchParams {
  const K = CALLS_LIST_PARAM_KEYS
  const params = new URLSearchParams()
  if (opts?.page) params.set(K.page, String(opts.page))
  if (opts?.limit) params.set(K.limit, String(opts.limit))
  if (opts?.search) params.set(K.search, opts.search)
  if (opts?.direction) params.set(K.direction, opts.direction)
  if (opts?.assignedTo) params.set(K.assignedTo, String(opts.assignedTo))
  if (opts?.sort) params.set(K.sort, opts.sort)
  if (opts?.order) params.set(K.order, opts.order)
  if (opts?.outcome) params.set(K.outcome, opts.outcome)
  if (opts?.has_recording) params.set(K.hasRecording, '1')
  if (opts?.has_transcript) params.set(K.hasTranscript, '1')
  return params
}
